import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react'
import * as THREE from 'three'
import { OrbitControls } from 'three/addons/controls/OrbitControls.js'
import { SparkRenderer, SplatMesh } from '@sparkjsdev/spark'
import { downloadCanvasScreenshot, aiEnhanceCanvas } from '../../utils/captureTools.jsx'
import { useXRSupport, useXRSession, setupXRControllers, XRButton, ModelTransformPanel, loadVariantLaunchSDK } from './DualgsViewer-XR.jsx'
import { installSplatXrProfile } from './splatXrProfile.js'

// Centred status message for the AR dom-overlay (tracking / tap-to-place hints).
const arHintStyle = {
  position: 'absolute',
  top: 24,
  left: '50%',
  transform: 'translateX(-50%)',
  padding: '10px 16px',
  borderRadius: 999,
  background: 'rgba(18,18,18,0.7)',
  backdropFilter: 'blur(8px)',
  border: '1px solid rgba(255,255,255,0.18)',
  color: '#fff',
  fontSize: 14,
  whiteSpace: 'nowrap',
  pointerEvents: 'none',
}

// ===== Preloader =====
function addLoader(container) {
  const existing = container.querySelector('#_spark_loader')
  if (existing) existing.remove()

  const wrapper = document.createElement('div')
  wrapper.id = '_spark_loader'
  wrapper.style.cssText = 'position:absolute;z-index:99999;inset:0;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:8px;pointer-events:none;background:rgba(0,0,0,0.4);'

  const label = document.createElement('div')
  label.id = '_spark_loader_label'
  label.style.cssText = 'color:#fff;font-size:14px;'
  label.textContent = 'Loading 0%'

  const track = document.createElement('div')
  track.style.cssText = 'width:200px;height:6px;background:rgba(255,255,255,0.2);border-radius:3px;overflow:hidden;'

  const bar = document.createElement('div')
  bar.id = '_spark_loader_bar'
  bar.style.cssText = 'width:0%;height:100%;background:#fff;border-radius:3px;transition:width 0.15s;'

  track.appendChild(bar)
  wrapper.appendChild(label)
  wrapper.appendChild(track)
  container.appendChild(wrapper)
}

function updateLoader(progress) {
  const bar = document.getElementById('_spark_loader_bar')
  const label = document.getElementById('_spark_loader_label')
  if (bar) bar.style.width = `${progress}%`
  if (label) label.textContent = `Loading ${Math.round(progress)}%`
}

function removeLoader() {
  const el = document.getElementById('_spark_loader')
  if (el?.parentNode) el.parentNode.removeChild(el)
}

async function preloadSplatsSequential(container, files) {
  addLoader(container)
  const map = {}
  const total = files.length
  let loaded = 0

  const promises = files.map((file) =>
    new Promise((resolve, reject) => {
      map[file] = new SplatMesh({
        url: file,
        onLoad: () => {
          loaded++
          updateLoader((loaded / total) * 100)
          resolve()
        },
        onError: (err) => {
          loaded++
          updateLoader((loaded / total) * 100)
          console.error(`Failed to load: ${file}`, err)
          reject(err)
        },
      })
    }).catch(() => {})
  )

  await Promise.all(promises)
  removeLoader()
  return map
}

// ===== Static scene layer =====

/** Identity value for a layer transform. Also the correct value when the scene
 *  3DGS was solved from the same COLMAP sparse/0 as the frames. */
export const IDENTITY_LAYER_TRANSFORM = Object.freeze({
  position: Object.freeze({ x: 0, y: 0, z: 0 }),
  quaternion: Object.freeze({ x: 0, y: 0, z: 0, w: 1 }),
  scale: 1,
})

/**
 * Write a persisted layer transform onto a THREE.Object3D.
 *
 * Scale is a SCALAR on purpose. Spark's SplatTransformer collapses a decomposed
 * world scale to (x + y + z) / 3, so a non-uniform scale in the graph is
 * silently averaged and would not render as authored.
 */
export function applyLayerTransform(obj, t) {
  if (!obj) return
  const src = t || IDENTITY_LAYER_TRANSFORM
  const p = src.position || IDENTITY_LAYER_TRANSFORM.position
  const q = src.quaternion || IDENTITY_LAYER_TRANSFORM.quaternion
  const s = typeof src.scale === 'number' && src.scale > 0 ? src.scale : 1
  obj.position.set(Number(p.x) || 0, Number(p.y) || 0, Number(p.z) || 0)
  obj.quaternion.set(Number(q.x) || 0, Number(q.y) || 0, Number(q.z) || 0,
                     q.w === undefined ? 1 : Number(q.w) || 0)
  if (obj.quaternion.lengthSq() < 1e-12) obj.quaternion.identity()
  else obj.quaternion.normalize()
  obj.scale.setScalar(s)
}

/** Read a layer transform back off a THREE.Object3D in the persisted shape. */
export function readLayerTransform(obj) {
  if (!obj) return { ...IDENTITY_LAYER_TRANSFORM }
  const round = (n) => Math.round(n * 1e6) / 1e6
  return {
    position: { x: round(obj.position.x), y: round(obj.position.y), z: round(obj.position.z) },
    quaternion: {
      x: round(obj.quaternion.x), y: round(obj.quaternion.y),
      z: round(obj.quaternion.z), w: round(obj.quaternion.w),
    },
    scale: round(obj.scale.x),
  }
}

/**
 * Load the persistent scene layer. Kept out of preloadSplatsSequential so a
 * failure here is visible and non-fatal: the sequence still plays without it.
 * Note `onError` is not a SplatMesh option, so we gate on the initialized
 * promise instead.
 */
async function loadStaticLayer(url) {
  if (!url) return null
  const mesh = new SplatMesh({ url, editable: false })
  const [settled] = await Promise.allSettled([mesh.initialized])
  if (settled.status === 'rejected') {
    console.error('[DualgsViewer] static layer failed to load, continuing without it:', url, settled.reason)
    return null
  }
  // Strip spherical harmonics. Spark re-runs the whole generate pass for any
  // mesh whose packedSplats carry sh1 on EVERY camera move; a degree-0 mesh is
  // generated once and then skipped. The constructor's own post-load
  // updateGenerator() already ran with the default maxSh, so the sh*Texture
  // objects may exist too and must go with the arrays.
  const extra = mesh.packedSplats?.extra
  if (extra) {
    let stripped = false
    for (const k of ['sh1', 'sh2', 'sh3', 'sh1Texture', 'sh2Texture', 'sh3Texture']) {
      if (extra[k] !== undefined) { delete extra[k]; stripped = true }
    }
    if (stripped) {
      mesh.maxSh = 0
      mesh.updateGenerator?.()
    }
  }
  // Local transform is identity — the authored pose lives on the staticLayer
  // Group, and the axis fix lives on contentRoot.
  mesh.position.set(0, 0, 0)
  mesh.quaternion.identity()
  return mesh
}

// ===== Config =====
const TOTAL_FRAMES = 150
const FPS = 30
const POSITION_OFFSET = new THREE.Vector3(-0.55, 1.20, 0.52)

// Offset of the model's visual centre from its LOCAL ORIGIN, in model units
// (at scale 1). Splat captures often have their geometry far from the origin,
// which makes the model "float" away from the AR reticle when anchored. We
// subtract this offset on placement so the model lands on the reticle.
//
// Default = inverse of POSITION_OFFSET (the desktop framing offset), which is a
// good first guess; tune X/Y/Z so the model sits correctly on the surface
// (raise Y so its base rests on the floor instead of being half-buried).
//
// TODO: read this per-model from manifest.json (e.g. { "arOffset": [x, y, z] })
// and pass it in via the `arOffset` prop instead of using this constant.
//
// These are the SuperSplat editor "Position" + "Rotation" values that make the
// model sit grounded/upright at the origin: import a frame .spz into SuperSplat,
// nudge Position/Rotation until it rests on 0, then paste the numbers here.
// On AR placement we apply this transform so the model lands on the reticle.
const MODEL_AR_OFFSET = [-0.05, 1.99, 0.9]
// SuperSplat "Rotation" in DEGREES (Euler XYZ). Future: manifest.json arRotation.
const MODEL_AR_ROTATION = [-6.45, 8.08, 3.45]

const PLY_path = '_ply/point_cloud_'
const PLY_FILES = Array.from({ length: TOTAL_FRAMES }, (_, index) =>
  `${PLY_path}${String(index + 100).padStart(3, '0')}.spz`
)

// ===== Camera path =====
// const CAMERA_PATH = [...camerasData].sort((a, b) => a.time - b.time)
// const CAMERA_PATH_DURATION = CAMERA_PATH.length > 0 ? CAMERA_PATH[CAMERA_PATH.length - 1].time : 0
// console.log('Camera path loaded:', CAMERA_PATH.length, 'keyframes, duration:', CAMERA_PATH_DURATION, 'seconds')

// Convert rotation matrix + position to an OrbitControls target.
// R is the 3×3 world-to-camera rotation matrix (COLMAP / OpenCV convention).
// Forward direction in world space = R^T * [0,0,1] = row 2 of R.
function rotMatToTarget(posVec, R) {
  const fwd = new THREE.Vector3(R[2][0], R[2][1], R[2][2])
  return posVec.clone().add(fwd)
}

// ===== Toggle Switch =====
function ToggleSwitch({ checked, onChange }) {
  return (
    <span
      onClick={onChange}
      style={{
        display: 'inline-block',
        width: 34, height: 18,
        borderRadius: 9,
        background: checked ? 'rgba(100,220,100,0.5)' : 'rgba(255,255,255,0.2)',
        border: `1px solid ${checked ? 'rgba(100,220,100,0.8)' : 'rgba(255,255,255,0.3)'}`,
        position: 'relative',
        transition: 'background 0.2s, border-color 0.2s',
        flexShrink: 0,
        cursor: 'pointer',
      }}
    >
      <span style={{
        position: 'absolute',
        top: 2,
        left: checked ? 18 : 2,
        width: 12, height: 12,
        borderRadius: '50%',
        background: '#fff',
        transition: 'left 0.2s',
      }} />
    </span>
  )
}

// ===== Player =====
const SplatSequencePlayer = forwardRef(function SplatSequencePlayer({
  className,
  showFps = false,
  modelUrl = null,
  frameUrls = null,   // array of per-frame URLs for sequence playback (.4dms decoded frames)
  fps: fpsProp = null,
  showUi = true,
  showTimeline,             // gates timeline scrubber + play/pause; defaults to showUi
  cameraPosition = null,    // [x, y, z] — externally controlled camera position
  cameraQuaternion = null,  // { w, x, y, z }
  onCameraChange = null,    // (position, quaternion) — fired when user drags the camera
  onCanvasReady = null,     // (canvas) — fired once after the WebGL canvas is mounted
  audioUrl = null,          // optional looping soundtrack synced with play/pause + seek
  staticUrl = null,         // optional persistent scene 3DGS (.spz/.ply); never swapped per frame
  staticTransform = null,   // { position:{x,y,z}, quaternion:{x,y,z,w}, scale:number } — pose of the
                            // scene's frame inside the performer's frame. null / omitted = identity,
                            // which is correct when both were solved from the same COLMAP sparse/0.
  enableXR = false,         // enable WebXR: renderer.xr.enabled + VRButton/ARButton overlay
  arOffset = MODEL_AR_OFFSET,     // [x,y,z] SuperSplat grounding position; future: manifest.json
  arRotation = MODEL_AR_ROTATION, // [x,y,z] SuperSplat grounding rotation (degrees, Euler XYZ)
}, ref) {
  // Resolve playback source: explicit sequence > single static file > built-in demo PLY_FILES.
  const sequenceFiles = Array.isArray(frameUrls) && frameUrls.length > 0 ? frameUrls : null
  const filesList = sequenceFiles ?? (modelUrl ? [modelUrl] : PLY_FILES)
  const totalFrames = filesList.length
  const playFps = fpsProp && fpsProp > 0 ? fpsProp : FPS
  const frameInterval = 1000 / playFps
  const isStaticSingle = !sequenceFiles && !!modelUrl
  // Static single-file models have nothing to scrub through, so suppress the
  // timeline + play/pause regardless of the prop.
  const showTimelineEffective = !isStaticSingle && (showTimeline ?? showUi)
  const formatTimeLocal = (frame) => {
    const totalSec = Math.floor(frame / playFps)
    const m = Math.floor(totalSec / 60)
    const s = totalSec % 60
    return `${m}:${s.toString().padStart(2, '0')}`
  }
  const canvasRef = useRef(null)
  const arOverlayRef = useRef(null) // dom-overlay root for the AR session (#overlay)
  const audioRef = useRef(null)
  const rendererRef = useRef(null)
  const sceneRef = useRef(null)
  const cameraRef = useRef(null)
  const controlsRef = useRef(null)
  const splatsRef = useRef({})
  const currentFrameRef = useRef(0)
  const frameRef = useRef(null)
  const animationIdRef = useRef(null)
  const isPlayingRef = useRef(true)
  const recordingSizeRef = useRef(null)
  const mediaRecorderRef = useRef(null)
  const switchFrameRef = useRef(null)   // exposed so seek works outside the effect
  const skipRenderRef = useRef(false)   // true while frame-by-frame recording owns the renderer
  const recordingCancelRef = useRef(null) // call to abort the frame loop early
  // Shared with the animate loop so timeline scrubs can shift the clock.
  const playbackBaseRef = useRef(performance.now())
  const pauseAtRef = useRef(null)
  const isCamPathPlayingRef = useRef(false)
  const camPathStartRef = useRef(0)
  const programmaticCameraRef = useRef(false)  // suppresses onCameraChange while we drive the camera
  // Keep latest callbacks in refs so the mount-only effect can always reach them.
  const onCameraChangeRef = useRef(onCameraChange)
  const onCanvasReadyRef = useRef(onCanvasReady)
  onCameraChangeRef.current = onCameraChange
  onCanvasReadyRef.current = onCanvasReady

  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)
  const [fps, setFps] = useState(0)
  const [isPlaying, setIsPlaying] = useState(true)
  const [currentFrame, setCurrentFrame] = useState(0)
  const [showSettings, setShowSettings] = useState(false)
  const [showTransform, setShowTransform] = useState(false)
  // AR placement flow status: null | 'searching' | 'ready' | 'placed'
  const [arStatus, setArStatus] = useState(null)
  const [screenshotWidth, setScreenshotWidth] = useState(1920)
  const [screenshotHeight, setScreenshotHeight] = useState(1080)
  const [isRecording, setIsRecording] = useState(false)
  const [isEnhancing, setIsEnhancing] = useState(false)
  const [enhancingDots, setEnhancingDots] = useState(3)
  const [isCamPathPlaying, setIsCamPathPlaying] = useState(false)
  const [isMuted, setIsMuted] = useState(false)
  // Scene graph (see the mount effect for the full rationale):
  //   modelAnchorRef   world placement — AR reticle, XR grab, ModelTransformPanel
  //   └ contentRootRef COLMAP→viewer axis fix (π about +X), set once, shared by both layers
  //     ├ sequenceLayerRef  the swapped per-frame meshes; kept at identity
  //     └ staticLayerRef    the persistent scene 3DGS; carries the authored relative transform
  const modelAnchorRef = useRef(null)
  const contentRootRef = useRef(null)
  const sequenceLayerRef = useRef(null)
  const staticLayerRef = useRef(null)
  const staticMeshRef = useRef(null)
  // Latest transform prop, read by the mount-only effect and by the live setter.
  const staticTransformRef = useRef(staticTransform)
  staticTransformRef.current = staticTransform

  // WebXR — support probing, session lifecycle, controller grab, and the
  // toolbar button all live in DualgsViewer-XR.jsx. We just wire refs in and
  // consume the return values.
  const xrSupport = useXRSupport(enableXR)
  const { xrActive, enterXR, exitXR, grabRef: xrGrabRef } = useXRSession({
    rendererRef,
    canvasRef,
    modelAnchorRef,
    overlayRef: arOverlayRef,
  })
  // Load the Variant Launch SDK on demand (iOS + this XR viewer mounted) so it
  // doesn't count a "launch" for every page view. No-op off-iOS / already loaded.
  useEffect(() => {
    if (enableXR) loadVariantLaunchSDK()
  }, [enableXR])
  // Auto-fade controls when the user goes idle (only applies when the timeline
  // is shown — per the spec, this is a sequence-only behavior).
  const [uiVisible, setUiVisible] = useState(true)
  const idleTimerRef = useRef(null)

  const scheduleHideUi = useCallback(() => {
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current)
    idleTimerRef.current = setTimeout(() => setUiVisible(false), 1500)
  }, [])

  const handleViewerMouseMove = useCallback(() => {
    if (!showTimelineEffective) return
    setUiVisible(true)
    scheduleHideUi()
  }, [showTimelineEffective, scheduleHideUi])

  useEffect(() => {
    if (showTimelineEffective) {
      setUiVisible(true)
      scheduleHideUi()
      return () => {
        if (idleTimerRef.current) {
          clearTimeout(idleTimerRef.current)
          idleTimerRef.current = null
        }
      }
    }
    // No timeline → keep the right-side buttons permanently visible.
    setUiVisible(true)
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current)
      idleTimerRef.current = null
    }
  }, [showTimelineEffective, scheduleHideUi])

  // Enhancing dots animation
  useEffect(() => {
    if (!isEnhancing) return
    const id = setInterval(() => setEnhancingDots((d) => (d % 3) + 1), 500)
    return () => clearInterval(id)
  }, [isEnhancing])

  // Debug: poll audio progress + state at 2Hz.
  // useEffect(() => {
  //   if (!audioUrl) return
  //   const id = setInterval(() => {
  //     const a = audioRef.current
  //     if (!a) return
  //     const cur = a.currentTime.toFixed(3)
  //     const dur = Number.isFinite(a.duration) ? a.duration.toFixed(3) : '?'
  //     console.log(`[audio] ${cur}s / ${dur}s · paused=${a.paused} · muted=${a.muted} · ready=${a.readyState}`)
  //   }, 500)
  //   return () => clearInterval(id)
  // }, [audioUrl])

  // Sync the optional soundtrack with play/pause. When autoplay is blocked
  // we also pause the *visual* so a single tap of the Play button starts
  // audio and video together — otherwise a tap on the (still playing)
  // Pause button would briefly start audio then immediately stop it.
  //
  // Right before play() we re-anchor audio.currentTime to the visual frame.
  // That covers two cases the scrub handler alone can't: (a) the scrub
  // happened while audio metadata wasn't loaded yet (no duration → seek
  // skipped), (b) the autoplay-blocked startup left currentTime at 0 even
  // though the user has since scrubbed elsewhere.
  useEffect(() => {
    const audio = audioRef.current
    if (!audio) return
    if (isPlaying) {
      const targetSec = currentFrameRef.current / playFps
      try {
        audio.currentTime = (Number.isFinite(audio.duration) && audio.duration > 0)
          ? targetSec % audio.duration
          : targetSec
      } catch { /* element not ready; ignored */ }
      audio.play().catch(() => {
        // Browsers block unmuted autoplay without a prior user gesture, but
        // muted autoplay is always allowed. Fall back to that and expose the
        // muted state so the user can toggle sound on with a click.
        audio.muted = true
        setIsMuted(true)
        audio.play().catch(() => { /* even muted failed; give up on audio */ })
      })
    } else {
      audio.pause()
    }
  }, [isPlaying, audioUrl, playFps])

  // Initialize Three.js scene
  // ── Play / Pause ──────────────────────────────────────────────────────────
  const togglePlay = useCallback(() => {
    const next = !isPlayingRef.current
    isPlayingRef.current = next
    setIsPlaying(next)
  }, [])

  // ── Mute / Unmute ─────────────────────────────────────────────────────────
  // A real click counts as user-activation, so unmuting after the muted
  // autoplay fallback Just Works™.
  const toggleMute = useCallback(() => {
    const audio = audioRef.current
    if (!audio) return
    audio.muted = !audio.muted
    setIsMuted(audio.muted)
    // Safety net: the muted-autoplay fallback in the isPlaying useEffect can
    // silently fail (some browsers reject back-to-back play() calls when the
    // first one is still settling its rejection). The click is a real user
    // gesture, so kick playback off now if the element is still paused —
    // and align to the current visual frame so audio doesn't restart from 0
    // while the picture is already mid-sequence.
    if (isPlayingRef.current && audio.paused) {
      const targetSec = currentFrameRef.current / playFps
      try {
        audio.currentTime = (Number.isFinite(audio.duration) && audio.duration > 0)
          ? targetSec % audio.duration
          : targetSec
      } catch { /* element not ready; ignored */ }
      audio.play().catch(() => {})
    }
  }, [playFps])

  // ── Timeline seek ─────────────────────────────────────────────────────────
  const handleTimelineClick = useCallback((e) => {
    const rect = e.currentTarget.getBoundingClientRect()
    const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width))
    const index = Math.min(totalFrames - 1, Math.floor(ratio * totalFrames))
    currentFrameRef.current = index
    setCurrentFrame(index)
    if (switchFrameRef.current && sceneRef.current) {
      switchFrameRef.current(sceneRef.current, index)
    }
    // Shift the playback clock so the animate loop doesn't snap us right back.
    // While paused, the clock is "frozen" at pauseAt — anchor the shift there
    // so resume picks up from the seeked frame.
    const reference = pauseAtRef.current ?? performance.now()
    playbackBaseRef.current = reference - (index / playFps) * 1000
    // Audio follows the scrubber. Wrap into [0, duration) so a seek past the
    // clip's length (when audio is shorter than the sequence) still lands in
    // a valid range under loop=true.
    const audio = audioRef.current
    if (audio && Number.isFinite(audio.duration) && audio.duration > 0) {
      const target = (index / playFps) % audio.duration
      // const seekableStr = audio.seekable.length > 0
      //   ? `[${audio.seekable.start(0).toFixed(3)}, ${audio.seekable.end(0).toFixed(3)}]`
      //   : 'EMPTY'
      audio.currentTime = target
      const onSeeked = () => {
        audio.removeEventListener('seeked', onSeeked)
      }
      audio.addEventListener('seeked', onSeeked)
    }
  }, [totalFrames, playFps])

  // Render to the renderer's canvas at a custom size, run `fn` (which captures pixels),
  // then restore the original size. fn may return a Promise; the caller awaits it.
  const withRenderAtSize = useCallback((width, height, fn) => {
    const renderer = rendererRef.current
    const camera = cameraRef.current
    const scene = sceneRef.current
    if (!renderer || !camera || !scene) return null

    const origWidth = renderer.domElement.width
    const origHeight = renderer.domElement.height

    renderer.setSize(width, height)
    camera.aspect = width / height
    camera.updateProjectionMatrix()
    renderer.render(scene, camera)

    const result = fn(renderer.domElement)

    renderer.setSize(origWidth, origHeight)
    camera.aspect = origWidth / origHeight
    camera.updateProjectionMatrix()

    return result
  }, [])

  // ── Screenshot ────────────────────────────────────────────────────────────
  const takeScreenshot = useCallback(() => {
    withRenderAtSize(screenshotWidth, screenshotHeight, (canvas) => {
      downloadCanvasScreenshot(canvas, { width: screenshotWidth, height: screenshotHeight })
    })
  }, [withRenderAtSize, screenshotWidth, screenshotHeight])

  // ── AI Enhance ────────────────────────────────────────────────────────────
  const handleAiEnhance = useCallback(async () => {
    const promise = withRenderAtSize(screenshotWidth, screenshotHeight, (canvas) =>
      aiEnhanceCanvas(canvas, { width: screenshotWidth, height: screenshotHeight })
    )
    if (!promise) return

    setIsEnhancing(true)
    try {
      await promise
    } catch (err) {
      console.error('AI enhance failed:', err)
      alert(`AI enhance failed: ${err.message}`)
    } finally {
      setIsEnhancing(false)
    }
  }, [withRenderAtSize, screenshotWidth, screenshotHeight])

  // Resize the renderer + camera aspect to a fixed output size (used by external
  // recorders so VideoFrame(canvas) matches the encoder's configured resolution).
  // Mirrors the size-swap dance the internal record button does.
  const setRenderSize = useCallback((width, height) => {
    const renderer = rendererRef.current
    const camera = cameraRef.current
    if (!renderer || !camera) return
    if (!recordingSizeRef.current) {
      recordingSizeRef.current = {
        origWidth: renderer.domElement.width,
        origHeight: renderer.domElement.height,
      }
    }
    renderer.setSize(width, height)
    camera.aspect = width / height
    camera.updateProjectionMatrix()
  }, [])

  const clearRenderSize = useCallback(() => {
    const renderer = rendererRef.current
    const camera = cameraRef.current
    const saved = recordingSizeRef.current
    if (!renderer || !camera || !saved) return
    renderer.setSize(saved.origWidth, saved.origHeight)
    camera.aspect = saved.origWidth / saved.origHeight
    camera.updateProjectionMatrix()
    recordingSizeRef.current = null
  }, [])

  // ── Static scene layer: live control for the admin alignment editor ───────
  // Driving the Object3D directly (rather than through React state) keeps a
  // drag at interactive rates: three writes and Spark re-generates only the
  // moved mesh, with no re-sort, because the accumulator mapping is unchanged.
  const getLayerObject = useCallback((name) => (
    name === 'static' ? staticLayerRef.current
      : name === 'sequence' ? sequenceLayerRef.current
      : name === 'content' ? contentRootRef.current
      : name === 'anchor' ? modelAnchorRef.current
      : null
  ), [])

  const setLayerTransform = useCallback((name, t) => {
    applyLayerTransform(getLayerObject(name), t)
  }, [getLayerObject])

  const getLayerTransform = useCallback((name) => readLayerTransform(getLayerObject(name)), [getLayerObject])

  /** Rotate a layer by `angle` radians about `axis` in the PARENT frame.
   *  Incremental premultiply, never an Euler decompose — no gimbal artifacts
   *  and no jumping fields at the poles. */
  const rotateLayer = useCallback((name, axis, angle) => {
    const obj = getLayerObject(name)
    if (!obj) return
    obj.quaternion.premultiply(
      new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(axis[0], axis[1], axis[2]).normalize(), angle),
    )
  }, [getLayerObject])

  /** Show/hide the static layer for an A/B check. Uses add/remove, never
   *  `visible = false`: a hidden SplatMesh left in the scene makes Spark treat
   *  the accumulator as dirty every frame and re-sort continuously. */
  const setStaticVisible = useCallback((show) => {
    const layer = staticLayerRef.current
    const mesh = staticMeshRef.current
    if (!layer || !mesh) return
    const attached = layer.children.includes(mesh)
    if (show && !attached) layer.add(mesh)
    else if (!show && attached) layer.remove(mesh)
  }, [])

  const hasStaticLayer = useCallback(() => !!staticMeshRef.current, [])

  useImperativeHandle(ref, () => ({
    aiEnhance: handleAiEnhance,
    takeScreenshot,
    setRenderSize,
    clearRenderSize,
    // Static scene layer
    setLayerTransform,
    getLayerTransform,
    rotateLayer,
    setStaticVisible,
    hasStaticLayer,
  }), [handleAiEnhance, takeScreenshot, setRenderSize, clearRenderSize,
       setLayerTransform, getLayerTransform, rotateLayer, setStaticVisible, hasStaticLayer])

  // Apply an externally-changed staticTransform prop (the mount effect handles
  // the initial value, which may arrive before the layer exists).
  useEffect(() => {
    applyLayerTransform(staticLayerRef.current, staticTransform)
  }, [staticTransform])

  // ── Camera path ───────────────────────────────────────────────────────────
  const toggleCamPath = useCallback(() => {
    const next = !isCamPathPlayingRef.current
    isCamPathPlayingRef.current = next
    setIsCamPathPlaying(next)
    if (next) {
      camPathStartRef.current = performance.now()
      if (controlsRef.current) controlsRef.current.enabled = false
    } else {
      if (controlsRef.current) controlsRef.current.enabled = true
    }
  }, [])

  // ── Record (frame-by-frame via switchFrame) ───────────────────────────────
  const handleRecord = useCallback(() => {
    if (isRecording) {
      // User clicked Stop — cancel the frame loop then stop the recorder
      if (recordingCancelRef.current) recordingCancelRef.current()
      const recorder = mediaRecorderRef.current
      if (recorder && recorder.state !== 'inactive') recorder.stop()
      return
    }

    const renderer = rendererRef.current
    const camera = cameraRef.current
    const scene = sceneRef.current
    if (!renderer || !camera || !scene) return

    const origWidth = renderer.domElement.width
    const origHeight = renderer.domElement.height
    recordingSizeRef.current = { origWidth, origHeight }

    // Stop the animation loop from rendering while we take over
    isPlayingRef.current = false
    setIsPlaying(false)
    skipRenderRef.current = true

    renderer.setSize(screenshotWidth, screenshotHeight)
    camera.aspect = screenshotWidth / screenshotHeight
    camera.updateProjectionMatrix()

    // Render frame 0 immediately so the canvas is not blank when the stream starts
    let frameIndex = 0
    currentFrameRef.current = 0
    setCurrentFrame(0)
    if (switchFrameRef.current) switchFrameRef.current(scene, 0)
    renderer.render(scene, camera)

    const stream = renderer.domElement.captureStream(playFps)
    const chunks = []
    const recorder = new MediaRecorder(stream, {
      mimeType: MediaRecorder.isTypeSupported('video/webm;codecs=vp9')
        ? 'video/webm;codecs=vp9'
        : 'video/webm',

      videoBitsPerSecond: 8_000_000, // 8 Mbps（建議範圍 5~20 Mbps）
    });

    recorder.ondataavailable = (e) => { if (e.data.size > 0) chunks.push(e.data) }

    recorder.onstop = () => {
      const blob = new Blob(chunks, { type: recorder.mimeType })
      const link = document.createElement('a')
      link.download = `recording_${screenshotWidth}x${screenshotHeight}_${Date.now()}.webm`
      link.href = URL.createObjectURL(blob)
      link.click()
      URL.revokeObjectURL(link.href)

      const saved = recordingSizeRef.current
      if (saved) {
        renderer.setSize(saved.origWidth, saved.origHeight)
        camera.aspect = saved.origWidth / saved.origHeight
        camera.updateProjectionMatrix()
      }
      recordingSizeRef.current = null
      skipRenderRef.current = false
      recordingCancelRef.current = null
      setIsRecording(false)
      mediaRecorderRef.current = null
    }

    let cancelled = false
    recordingCancelRef.current = () => { cancelled = true }

    const renderNextFrame = () => {
      if (cancelled) return

      if (switchFrameRef.current) switchFrameRef.current(scene, frameIndex)
      renderer.render(scene, camera)
      currentFrameRef.current = frameIndex
      setCurrentFrame(frameIndex)

      frameIndex++
      if (frameIndex >= totalFrames) {
        recorder.stop()
        return
      }
      setTimeout(renderNextFrame, frameInterval)
    }

    mediaRecorderRef.current = recorder
    recorder.start()
    setIsRecording(true)
    setTimeout(renderNextFrame, 0)
  }, [isRecording, screenshotWidth, screenshotHeight])

  // ── Three.js scene ────────────────────────────────────────────────────────
  useEffect(() => {
    if (!canvasRef.current) return

    const container = canvasRef.current

    // StrictMode dev double-mounts the effect; the first mount's preload is
    // async and resolves *after* cleanup has run. Without this flag the stale
    // resolve would start a second rAF loop on top of the live one, and both
    // would race setCurrentFrame — visible as time-display oscillation.
    let disposed = false
    let ownedSplats = {}

    const scene = new THREE.Scene()
    sceneRef.current = scene

    // All splat frames are added as children of this anchor (not directly to
    // the scene), so XR placement + the controller grab below can transform
    // the whole model as a single unit. Anchor sits at POSITION_OFFSET so
    // desktop rendering is visually unchanged from the pre-refactor code; per-
    // frame meshes now sit at their parent's origin instead of carrying the
    // offset themselves.
    const modelAnchor = new THREE.Group()
    modelAnchor.name = 'modelAnchor'
    modelAnchor.position.copy(POSITION_OFFSET)
    scene.add(modelAnchor)
    modelAnchorRef.current = modelAnchor

    // Two more Groups under the anchor. They cost Spark nothing — compileScene
    // only collects `node instanceof SplatGenerator`, so plain Groups are
    // invisible to generation and sorting.
    //
    // contentRoot carries the COLMAP→viewer axis fix. Frames used to carry it
    // themselves as `quaternion.set(1, 0, 0, 0)`, which in three's (x,y,z,w)
    // order is w=0 ⇒ π about +X, i.e. (x,y,z) ↦ (x,−y,−z) — NOT identity.
    // Hoisting it to a shared parent is what makes the static layer's default
    // transform the identity: a scene 3DGS solved from the same COLMAP
    // sparse/0 as the frames then lands on the performer with no authoring.
    // It cannot live on modelAnchor because AR placement overwrites the
    // anchor's quaternion wholesale.
    const contentRoot = new THREE.Group()
    contentRoot.name = 'contentRoot'
    contentRoot.quaternion.set(1, 0, 0, 0)
    modelAnchor.add(contentRoot)
    contentRootRef.current = contentRoot

    // sequenceLayer holds the swapped frames and stays at identity; staticLayer
    // holds the persistent scene and carries the authored relative transform.
    // Only their difference is observable, so we always author on staticLayer.
    const sequenceLayer = new THREE.Group()
    sequenceLayer.name = 'sequenceLayer'
    contentRoot.add(sequenceLayer)
    sequenceLayerRef.current = sequenceLayer

    const staticLayer = new THREE.Group()
    staticLayer.name = 'staticLayer'
    applyLayerTransform(staticLayer, staticTransformRef.current)
    contentRoot.add(staticLayer)
    staticLayerRef.current = staticLayer

    // For debugging: a simple cube to verify camera alignment and rendering
    // const cube = new THREE.Mesh(
    //   new THREE.BoxGeometry(),
    //   new THREE.MeshNormalMaterial()
    // )
    // scene.add(cube)
    // Renderer — initial size 1x1, ResizeObserver will set correct size
    // alpha:true so AR passthrough can show through the clear color. We keep an
    // opaque black clear for desktop/VR (look unchanged); the XR hook flips the
    // clear alpha to 0 on AR session entry and back to 1 on exit.
    // Gaussian edges are already blended by Spark. MSAA adds substantial
    // stereo rendering cost without improving those edges.
    const renderer = new THREE.WebGLRenderer({ preserveDrawingBuffer: true, antialias: false, alpha: true })
    renderer.setClearColor(0x000000, 1)
    renderer.setSize(1, 1)
    container.appendChild(renderer.domElement)
    rendererRef.current = renderer

    // Own the Spark instance instead of relying on SplatMesh's automatic one,
    // so entering XR can keep a complete 360-degree draw list. This still
    // renders and follows the headset on every XR animation callback.
    const spark = new SparkRenderer({ renderer, preUpdate: false })
    scene.add(spark)
    const removeXrProfile = enableXR ? installSplatXrProfile(renderer, spark) : () => {}

    // All XR plumbing (renderer.xr.enabled, controllers, selectstart/end
    // listeners, one-hand drag / two-hand pinch state machine) lives in
    // DualgsViewer-XR.jsx. We just receive the per-tick updater and call it
    // in the animate loop before render. No-op when !enableXR.
    const { updateXrGrab } = setupXRControllers({
      renderer,
      scene,
      modelAnchor,
      // AR auto-fit must size on the performer alone; a room-sized static layer
      // under the same anchor would otherwise shrink the model to a speck.
      measureTarget: sequenceLayer,
      enableXR,
      grabRef: xrGrabRef,
      onArStatus: setArStatus,
      arOffset,
      arRotation,
    })

    // Hand the canvas to the parent (e.g. for screenshot/recording capture).
    if (onCanvasReadyRef.current) onCanvasReadyRef.current(renderer.domElement)

    const camera = new THREE.PerspectiveCamera(39.6, 1, 0.1, 1000)
    cameraRef.current = camera

    const controls = new OrbitControls(camera, renderer.domElement)
    // 設定 position
    camera.position.set(7.358891487121582, 4.958309173583984, -6.925790786743164)

    controls.target.set(0, 0, 0)
    controls.minDistance = 1.5
    controls.maxDistance = 13.3
    controls.enablePan = true
    controls.panSpeed = 1.0

    // Emit user-driven camera changes upward. Skip when we're driving the camera ourselves.
    controls.addEventListener('change', () => {
      if (programmaticCameraRef.current) return
      const cb = onCameraChangeRef.current
      if (!cb) return
      const p = camera.position
      const q = camera.quaternion
      cb([p.x, p.y, p.z], { w: q.w, x: q.x, y: q.y, z: q.z })
    })
    // const min = { x: -1, y: -1, z: -3.3 }
    // const max = { x:  1, y:  1, z: -1 }
    // controls.addEventListener('change', () => {
    //   const t = controls.target
    //   t.x = Math.max(min.x, Math.min(max.x, t.x))
    //   t.y = Math.max(min.y, Math.min(max.y, t.y))
    //   t.z = Math.max(min.z, Math.min(max.z, t.z))
    // })
    controls.update()
    controlsRef.current = controls

    let desktopControlsEnabled = controls.enabled
    const suspendDesktopControls = () => {
      desktopControlsEnabled = controls.enabled
      controls.enabled = false
    }
    const restoreDesktopControls = () => { controls.enabled = desktopControlsEnabled }
    renderer.xr.addEventListener('sessionstart', suspendDesktopControls)
    renderer.xr.addEventListener('sessionend', restoreDesktopControls)

    // Sample every rendered frame, but only publish React UI state twice a
    // second. Head tracking and rendering must not share this UI throttle.
    let lastRenderTime = null
    let averageFps = 0
    let lastFpsPublish = -Infinity
    const reportFps = (time) => {
      const delta = lastRenderTime === null ? 0 : time - lastRenderTime
      lastRenderTime = time
      if (delta <= 0) return
      const currentFps = 1000 / delta
      averageFps = averageFps ? averageFps * 0.9 + currentFps * 0.1 : currentFps
      if (time - lastFpsPublish >= 500) {
        lastFpsPublish = time
        setFps(Math.round(averageFps))
      }
    }

    // Resize handler — tracks container size, also sets initial size
    // Skip resize when recording at custom resolution
    const resizeObserver = new ResizeObserver((entries) => {
      if (recordingSizeRef.current) return
      const { width, height } = entries[0].contentRect
      if (width === 0 || height === 0) return
      camera.aspect = width / height
      camera.updateProjectionMatrix()
      renderer.setSize(width, height)
    })
    resizeObserver.observe(container)

    // Load splats then start animation
    function updateCameraPath(time) {
      if (renderer.xr.isPresenting) return
      if (!isCamPathPlayingRef.current || CAMERA_PATH.length < 2) return
      const elapsed = (time - camPathStartRef.current) / 1000
      const loopDuration = CAMERA_PATH_DURATION + 0.5 // brief pause before loop
      const t = elapsed % loopDuration

      // Find surrounding keyframes
      let a = CAMERA_PATH[0]
      let b = CAMERA_PATH[CAMERA_PATH.length - 1]
      for (let i = 0; i < CAMERA_PATH.length - 1; i++) {
        if (t >= CAMERA_PATH[i].time && t < CAMERA_PATH[i + 1].time) {
          a = CAMERA_PATH[i]
          b = CAMERA_PATH[i + 1]
          console.log('Camera path segment:', i, 't:', t.toFixed(2), 'a.time:', a.time, 'b.time:', b.time)
          break
        }
      }

      const alpha = b.time > a.time ? Math.min(1, (t - a.time) / (b.time - a.time)) : 0

      const posA = new THREE.Vector3(...a.position)
      const posB = new THREE.Vector3(...b.position)
      const pos = posA.clone().lerp(posB, alpha)

      const tgtA = rotMatToTarget(posA, a.rotation)
      const tgtB = rotMatToTarget(posB, b.rotation)
      const tgt = tgtA.clone().lerp(tgtB, alpha)

      camera.position.copy(pos)
      controls.target.copy(tgt)
      controls.update()

      // Sync model playback from the current segment's start keyframe
      const shouldPlay = a.model_playing
      if (isPlayingRef.current !== shouldPlay) {
        isPlayingRef.current = shouldPlay
        setIsPlaying(shouldPlay)
      }
    }

    const filesToLoad = filesList
    Promise.all([
      preloadSplatsSequential(container, filesToLoad),
      loadStaticLayer(staticUrl),
    ])
      .then(([loaded_splats, staticMesh]) => {
        if (disposed) {
          staticMesh?.dispose?.()
          for (const mesh of Object.values(loaded_splats)) mesh.dispose()
          return
        }
        ownedSplats = loaded_splats
        splatsRef.current = ownedSplats
        // Added once and never touched again: switchFrame only ever removes the
        // frame it tracks, and it lives on a different parent anyway.
        if (staticMesh) {
          staticLayer.add(staticMesh)
          staticMeshRef.current = staticMesh
        }
        setIsLoading(false)
        if (isStaticSingle) {
          // Single file: show it statically under sequenceLayer, so XR
          // placement / grab and the shared axis fix both still apply.
          const mesh = loaded_splats[modelUrl]
          if (mesh) {
            mesh.quaternion.identity()
            mesh.position.set(0, 0, 0)
            sequenceLayer.add(mesh)
          }
          const renderLoop = (time, frame) => {
            if (disposed) return
            // In XR the WebGLRenderer drives the loop via setAnimationLoop;
            // self-rescheduling would double up the callbacks.
            if (!enableXR) animationIdRef.current = requestAnimationFrame(renderLoop)
            updateCameraPath(time)
            if (!renderer.xr.isPresenting) controls.update()
            updateXrGrab(frame)
            renderer.render(scene, camera)
            reportFps(time)
          }
          if (enableXR) renderer.setAnimationLoop(renderLoop)
          else animationIdRef.current = requestAnimationFrame(renderLoop)
        } else {
          startAnimation(scene, renderer, camera)
        }
      })
      .catch((err) => {
        if (disposed) return
        console.error('preload error:', err)
        setError(err.message)
        setIsLoading(false)
      })

    function startAnimation(scene, renderer, camera) {
      let ticks = 0
      // playbackBaseRef is the performance.now() value that maps to frame 0;
      // pauseAtRef freezes playback time across pause/resume (and covers the
      // case where playback starts paused, so resume doesn't jump ahead).
      // Both live on refs so timeline scrubs from outside this closure can
      // re-anchor the clock — otherwise the next tick recomputes nowFrame
      // from the old base and immediately snaps the visual back.
      playbackBaseRef.current = performance.now()
      pauseAtRef.current = isPlayingRef.current ? null : performance.now()
      let prevPlaying = isPlayingRef.current

      // Seed the scene with the current frame before the first rAF tick —
      // Spark's SplatMesh only uploads after it's added, so otherwise the
      // canvas stays blank until the first scheduled switch lands on screen.
      switchFrame(scene, currentFrameRef.current)
      setCurrentFrame(currentFrameRef.current)

      const animate = (time, frame) => {
        if (disposed) {
          if (enableXR) renderer.setAnimationLoop(null)
          return
        }
        // In XR mode the WebGLRenderer drives the loop via setAnimationLoop;
        // self-rescheduling would double-up the callbacks.
        if (!enableXR) animationIdRef.current = requestAnimationFrame(animate)

        updateCameraPath(time)

        const playing = isPlayingRef.current
        if (playing !== prevPlaying) {
          if (playing && pauseAtRef.current !== null) {
            playbackBaseRef.current += performance.now() - pauseAtRef.current
            pauseAtRef.current = null
          } else if (!playing) {
            pauseAtRef.current = performance.now()
          }
          prevPlaying = playing
        }

        if (playing) {
          // Drive the index from playback time, then only swap when it
          // actually changes. The previous time-gated approach (>frameInterval)
          // fired unevenly against 60Hz rAF and caused visible stutter.
          //
          // Note: we deliberately do NOT pin audio.currentTime back to 0 at
          // the visual cycle wrap. Doing so breaks the "scrub to a late
          // frame then play" UX: audio would start at the seeked offset,
          // hit the wrap a few hundred ms later, and be yanked back to 0
          // — sounding as if it had restarted from 0 immediately. With
          // <audio loop> the soundtrack restarts on its own, and as long
          // as the clip length matches the sequence duration the two stay
          // in phase naturally.
          const nowFrame = Math.floor(((performance.now() - playbackBaseRef.current) / 1000) * playFps) % totalFrames
          if (nowFrame !== currentFrameRef.current) {
            currentFrameRef.current = nowFrame
            switchFrame(scene, nowFrame)
            ticks++
            if (ticks % 3 === 0) setCurrentFrame(nowFrame)
          }
        }

        updateXrGrab(frame)
        if (!skipRenderRef.current) renderer.render(scene, camera)

        reportFps(time)
      }

      if (enableXR) {
        renderer.setAnimationLoop(animate)
      } else {
        animationIdRef.current = requestAnimationFrame(animate)
      }
    }

    let frame
    function switchFrame(_scene, index) {
      // `_scene` is ignored — frames parent under `sequenceLayer`, a descendant
      // of `modelAnchor`, so XR placement / controller grab still transform them
      // as a unit. The param is preserved for the existing call sites.
      const splats = splatsRef.current
      const nextFrame = splats[filesList[index]]
      if (!nextFrame) return
      const layer = sequenceLayerRef.current
      if (!layer) return

      // Identity: the axis fix now lives on contentRoot, shared with the static
      // layer, so both are flipped by exactly the same rotation.
      nextFrame.quaternion.identity()
      nextFrame.position.set(0, 0, 0)

      if (frame && frame !== nextFrame && layer.children.includes(frame)) {
        layer.remove(frame)
      }

      layer.add(nextFrame)
      frameRef.current = nextFrame
      frame = nextFrame
    }

    switchFrameRef.current = switchFrame
    // Cleanup
    return () => {
      disposed = true
      removeLoader()
      resizeObserver.disconnect()
      if (animationIdRef.current !== null) cancelAnimationFrame(animationIdRef.current)
      if (enableXR) renderer.setAnimationLoop(null)
      renderer.xr.removeEventListener('sessionstart', suspendDesktopControls)
      renderer.xr.removeEventListener('sessionend', restoreDesktopControls)
      removeXrProfile()
      // Spark 0.1 has no renderer.dispose(). Stop new work and detach inputs;
      // its pending readback/sort can finish without touching disposed meshes.
      // Disposing the viewpoint here would race that asynchronous completion.
      spark.autoUpdate = false
      spark.defaultView.setAutoUpdate(false)
      scene.remove(spark)
      sequenceLayer.clear()
      for (const mesh of Object.values(ownedSplats)) mesh.dispose()
      if (splatsRef.current === ownedSplats) splatsRef.current = {}
      if (staticMeshRef.current) {
        staticLayer.remove(staticMeshRef.current)
        staticMeshRef.current.dispose?.()
        staticMeshRef.current = null
      }
      controls.dispose()
      renderer.dispose()
      if (renderer.domElement.parentNode) renderer.domElement.parentNode.removeChild(renderer.domElement)
    }
  }, [])

  // ── Sync externally-controlled camera (cameraPosition / cameraQuaternion) ─
  useEffect(() => {
    if (!cameraPosition || !cameraQuaternion) return
    const cam = cameraRef.current
    const ctrls = controlsRef.current
    if (!cam || !ctrls) return

    programmaticCameraRef.current = true
    cam.position.set(cameraPosition[0], cameraPosition[1], cameraPosition[2])
    cam.quaternion.set(
      cameraQuaternion.x ?? 0,
      cameraQuaternion.y ?? 0,
      cameraQuaternion.z ?? 0,
      cameraQuaternion.w ?? 1,
    )
    // OrbitControls always reorients the camera to look at `target`. Place the
    // target along the camera's forward axis so the supplied quaternion is
    // preserved on the next controls.update().
    const forward = new THREE.Vector3(0, 0, -1).applyQuaternion(cam.quaternion)
    const dist = Math.max(0.001, ctrls.target.distanceTo(cam.position) || 5)
    ctrls.target.copy(forward.multiplyScalar(dist).add(cam.position))
    ctrls.update()
    // Release on the next frame, after OrbitControls has fired its 'change'.
    requestAnimationFrame(() => { programmaticCameraRef.current = false })
  }, [cameraPosition, cameraQuaternion])

  // ── Shared button style ───────────────────────────────────────────────────
  const ctrlBtn = {
    background: 'rgba(255,255,255,0.1)',
    color: '#fff',
    padding: '6px 12px',
    borderRadius: 6,
    fontSize: '0.82rem',
    border: '1px solid rgba(255,255,255,0.18)',
    cursor: 'pointer',
    lineHeight: 1,
    display: 'flex',
    alignItems: 'center',
    gap: 4,
  }

  const divider = { height: 1, background: 'rgba(255,255,255,0.1)', margin: '2px 0' }

  const progress = totalFrames > 1 ? (currentFrame / (totalFrames - 1)) * 100 : 0

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div
      className={className}
      style={{ position: 'relative', width: '100%', height: '100%' }}
      onMouseMove={handleViewerMouseMove}
    >
      {/* Canvas container */}
      <div ref={canvasRef} style={{ width: '100%', height: '100%' }} />

      {/* Optional soundtrack — loop so it tracks the playback cycle */}
      {audioUrl && (
        <audio ref={audioRef} src={audioUrl} loop preload="auto" />
      )}

      {/* ── AR DOM overlay (#overlay) ──
          This is the WebXR dom-overlay root (see useXRSession). Variant Launch
          hides everything except this subtree during AR, so all AR UI lives
          here. Always mounted (stable node) and only shown while in AR. */}
      <div
        ref={arOverlayRef}
        id="overlay"
        style={{
          position: 'absolute',
          inset: 0,
          // Always mounted/visible (kept as a stable dom-overlay root); only
          // captures input while in AR. Empty + click-through otherwise.
          pointerEvents: xrActive ? 'auto' : 'none',
          color: '#fff',
          fontFamily: 'Inter, system-ui, sans-serif',
          zIndex: xrActive ? 40 : -1,
        }}
      >
        {/* Tracking prompt — move the phone to find a surface */}
        {xrActive && arStatus === 'searching' && (
          <div style={arHintStyle}>
            移動手機掃描周圍以尋找平面…
          </div>
        )}
        {/* Ready — reticle is visible, tap to grow the model */}
        {xrActive && arStatus === 'ready' && (
          <div style={arHintStyle}>
            點擊圓圈放置模型
          </div>
        )}
        {/* Placed — show the transform panel + a re-place button */}
        {xrActive && arStatus === 'placed' && (
          <>
            <ModelTransformPanel modelAnchorRef={modelAnchorRef} inAR />
            <button
              data-ar-ui
              onPointerDown={(e) => { e.preventDefault(); xrGrabRef.current?.replace?.() }}
              style={{
                position: 'absolute', bottom: 24, left: '50%', transform: 'translateX(-50%)',
                padding: '10px 18px', borderRadius: 999,
                border: '1px solid rgba(255,255,255,0.25)', background: 'rgba(18,18,18,0.7)',
                color: '#fff', fontSize: 14, pointerEvents: 'auto', backdropFilter: 'blur(8px)',
              }}
            >
              重新放置
            </button>
          </>
        )}
      </div>

      {/* ── UI overlay ── */}
      <div id="ui" style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>

        {/* Model transform panel (normal viewer) */}
        {showUi && showTransform && !xrActive && !isLoading && !error && (
          <ModelTransformPanel
            modelAnchorRef={modelAnchorRef}
            onClose={() => setShowTransform(false)}
          />
        )}

        {/* Error state */}
      {error && (
          <div style={{
            position: 'absolute', inset: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: 'rgba(0,0,0,0.8)', color: '#f44', fontSize: '1.2rem',
          }}>
          Error: {error}
        </div>
      )}

        {/* ── Settings Panel (floating, above controls) ── */}
        {showUi && showSettings && !isLoading && !error && (
          <div id="settingsPanel" style={{
            position: 'absolute',
            bottom: 125,
            right: 16,
            background: 'rgba(18,18,18,0.92)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255,255,255,0.14)',
            borderRadius: 10,
            padding: '12px 14px',
            display: 'flex',
            flexDirection: 'column',
            gap: 10,
            pointerEvents: uiVisible ? 'auto' : 'none',
            minWidth: 270,
            opacity: uiVisible ? 1 : 0,
            transition: 'opacity 0.3s ease',
          }}>

            {/* Resolution row */}
            <div className="settingsRow" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.8rem', flex: 1 }}>Resolution</span>
          <input
            type="number"
            value={screenshotWidth}
            onChange={(e) => setScreenshotWidth(Number(e.target.value))}
                style={{ width: 64, padding: '3px 6px', borderRadius: 4, border: '1px solid rgba(255,255,255,0.18)', background: 'rgba(255,255,255,0.07)', color: '#fff', fontSize: '0.8rem', textAlign: 'center' }}
          />
              <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.8rem' }}>×</span>
          <input
            type="number"
            value={screenshotHeight}
            onChange={(e) => setScreenshotHeight(Number(e.target.value))}
                style={{ width: 64, padding: '3px 6px', borderRadius: 4, border: '1px solid rgba(255,255,255,0.18)', background: 'rgba(255,255,255,0.07)', color: '#fff', fontSize: '0.8rem', textAlign: 'center' }}
              />
            </div>

            <div className="divider" style={divider} />

            {/* Screenshot / AI Enhance row */}
            <div className="settingsRow" style={{ display: 'flex', gap: 8 }}>
              <button onClick={takeScreenshot} style={{ ...ctrlBtn, flex: 1, justifyContent: 'center' }}>
                Screenshot
              </button>
              <button
                disabled={isEnhancing}
                onClick={handleAiEnhance}
                style={{
                  ...ctrlBtn,
                  flex: 1,
                  justifyContent: 'center',
                  background: isEnhancing ? 'rgba(100,150,255,0.3)' : 'rgba(100,150,255,0.15)',
                  border: '1px solid rgba(100,150,255,0.4)',
                  opacity: isEnhancing ? 0.7 : 1,
                  cursor: isEnhancing ? 'not-allowed' : 'pointer',
                  minWidth: 110,
                }}
              >
                {isEnhancing ? `Enhancing${'.'.repeat(enhancingDots)}` : 'AI Enhance'}
              </button>
            </div>
          </div>
        )}

        {/* ── Controls Wrap (bottom bar) ── */}
        {(showTimelineEffective || showUi) && !isLoading && !error && (
          <div id="controlsWrap" style={{
            position: 'absolute',
            bottom: 0, left: 0, right: 0,
            padding: '0 16px 16px',
            display: 'flex',
            flexDirection: 'column',
            gap: 6,
            pointerEvents: 'none',
            opacity: uiVisible ? 1 : 0,
            transition: 'opacity 0.3s ease',
          }}>

            {/* Timeline (gated on showTimeline) */}
            {showTimelineEffective && (
              <div
                id="timelineContainer"
                onClick={handleTimelineClick}
                style={{
                  position: 'relative',
                  height: 20,
                  display: 'flex',
                  alignItems: 'center',
                  cursor: 'pointer',
                  pointerEvents: 'auto',
                }}
              >
                {/* Track */}
                <div id="line" style={{
                  position: 'absolute',
                  left: 0, right: 0,
                  height: 4,
                  background: 'rgba(255,255,255,0.18)',
                  borderRadius: 2,
                  overflow: 'hidden',
                }}>
                  <div style={{
                    height: '100%',
                    width: `${progress}%`,
                    background: 'rgba(255,255,255,0.75)',
                    borderRadius: 2,
                    transition: 'width 0.05s linear',
                  }} />
                </div>

                {/* Handle */}
                <div id="handle" style={{
                  position: 'absolute',
                  left: `${progress}%`,
                  transform: 'translateX(-50%)',
                  width: 12, height: 12,
                  borderRadius: '50%',
                  background: '#fff',
                  boxShadow: '0 0 4px rgba(0,0,0,0.5)',
                  transition: 'left 0.05s linear',
                  pointerEvents: 'none',
                }} />

                {/* Time */}
                <div id="time" style={{
                  position: 'absolute',
                  right: 0,
                  top: -18,
                  color: 'rgba(255,255,255,0.55)',
                  fontSize: '0.7rem',
                  userSelect: 'none',
                }}>
                  {formatTimeLocal(currentFrame)} / {formatTimeLocal(Math.max(0, totalFrames - 1))}
                </div>
              </div>
            )}

            {/* Buttons Container */}
            <div id="buttonsContainer" style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              background: 'rgba(14,14,14,0.5)',
              backdropFilter: 'blur(10px)',
              padding: '8px 12px',
              borderRadius: 10,
              border: '1px solid rgba(255,255,255,0.1)',
              pointerEvents: 'auto',
            }}>

              {/* Left group — Play / Pause (showTimeline) / Cam (showUi) */}
              <div className="buttonGroup" style={{ display: 'flex', gap: 6 }}>
                {showTimelineEffective && (
                  <button
                    id={isPlaying ? 'pause' : 'play'}
                    onClick={togglePlay}
                    title={isPlaying ? 'Pause' : 'Play'}
                    style={{ ...ctrlBtn, minWidth: 36, justifyContent: 'center', fontSize: '1rem', padding: '4px 10px' }}
                  >
                    {isPlaying ? '⏸' : '▶'}
                  </button>
                )}
                {showUi && (
                  <button
                    onClick={toggleCamPath}
                    title="Log camera position to console"
                    style={{ ...ctrlBtn, fontSize: '0.82rem' }}
                  >
                    📍 Cam
                  </button>
                )}
                {showUi && (
                  <button
                    onClick={() => setShowTransform((v) => !v)}
                    title="Model transform (position / scale)"
                    style={{
                      ...ctrlBtn,
                      fontSize: '0.82rem',
                      background: showTransform ? 'rgba(255,255,255,0.22)' : ctrlBtn.background,
                      border: showTransform ? '1px solid rgba(255,255,255,0.35)' : ctrlBtn.border,
                    }}
                  >
                    ✥ XYZ
                  </button>
                )}
              </div>

              {/* Spacer */}
              <div style={{ flex: 1 }} />

              {/* XR enter / exit — see DualgsViewer-XR.jsx for the button + handlers. */}
              <XRButton
                enableXR={enableXR}
                xrSupport={xrSupport}
                xrActive={xrActive}
                onEnter={enterXR}
                onExit={exitXR}
                ctrlBtn={ctrlBtn}
              />


              {/* Mute toggle — shown whenever there's a soundtrack */}
              {audioUrl && showTimelineEffective && (
                <button
                  onClick={toggleMute}
                  title={isMuted ? 'Unmute' : 'Mute'}
                  style={{ ...ctrlBtn, minWidth: 36, justifyContent: 'center', fontSize: '1rem', padding: '4px 10px' }}
                >
                  {isMuted ? '🔇' : '🔊'}
                </button>
              )}

              {/* FPS display */}
              {showFps && showUi && (
                <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.75rem', minWidth: 52 }}>
                  {fps} FPS
                </span>
              )}

              {/* Right group — Settings / Record (showUi) */}
              {showUi && (
                <div className="buttonGroup" style={{ display: 'flex', gap: 6 }}>
                  <button
                    id="settings"
                    onClick={() => setShowSettings((v) => !v)}
                    title="Settings"
                    style={{
                      ...ctrlBtn,
                      minWidth: 36,
                      justifyContent: 'center',
                      fontSize: '1rem',
                      padding: '4px 10px',
                      background: showSettings ? 'rgba(255,255,255,0.22)' : ctrlBtn.background,
                      border: showSettings ? '1px solid rgba(255,255,255,0.35)' : ctrlBtn.border,
                    }}
                  >
                    ⚙
                  </button>

                  <button
                    id="record"
                    onClick={handleRecord}
                    title={isRecording ? 'Stop Recording' : 'Record'}
                    style={{
                      ...ctrlBtn,
                      background: isRecording ? 'rgba(220,50,50,0.5)' : ctrlBtn.background,
                      border: `1px solid ${isRecording ? 'rgba(255,100,100,0.5)' : 'rgba(255,255,255,0.18)'}`,
                    }}
                  >
                    {isRecording ? '⏹ Stop' : '⏺ Record'}
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
})

export default SplatSequencePlayer
